/**
 * Klient för admin-CRUD av POI/map-pins (S-13-01b).
 *
 * Wrappar:
 *  - `GET /api/admin/map-pins`           — lista
 *  - `GET /api/admin/map-pins/{id}`      — detalj
 *  - `POST /api/admin/map-pins`          — skapa
 *  - `PATCH /api/admin/map-pins/{id}`    — uppdatera
 *  - `DELETE /api/admin/map-pins/{id}`   — soft-delete
 *  - `POST /api/admin/map-pins/{id}/restore` — återställ
 *
 * Hard-rules (CLAUDE.md + avvikelse 7):
 *  - Cookie-baserad auth + CSRF-header på alla muterande anrop.
 *  - Inga råa databas-ID:n (UUID:s exponeras som strängar).
 *  - PII-detektor körs server-side; klient-side validering är
 *    duplicerad för UX (snabb feedback) men servern är källan av
 *    sanning.
 *  - Användartexter på svenska.
 */
import { csrfHeader } from '../auth/csrf';

const API_BASE = '/api/admin/map-pins';

export type PinType = 'defibrillator' | 'dog_waste_bin' | 'lighting' | 'notice';
export type PinKind = 'info' | 'notice';

export type PoiRecord = {
  id: string;
  pinType: PinType;
  kind: PinKind;
  latitude: number;
  longitude: number;
  title: string;
  body: string | null;
  activeFrom: string;
  activeTo: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
};

export type CreatePoiInput = {
  pinType: PinType;
  kind: PinKind;
  latitude: number;
  longitude: number;
  title: string;
  body: string | null;
  activeFrom: string | null;
  activeTo: string | null;
};

export type UpdatePoiInput = Omit<CreatePoiInput, 'activeFrom'> & {
  activeFrom: string;
};

export type ListResult =
  | { kind: 'ok'; items: PoiRecord[] }
  | { kind: 'unauthorized' }
  | { kind: 'forbidden' }
  | { kind: 'error'; message: string };

export type SaveResult =
  | { kind: 'ok'; item: PoiRecord }
  | { kind: 'unauthorized' }
  | { kind: 'forbidden' }
  | { kind: 'invalid'; field: string; message: string }
  | { kind: 'not_found' }
  | { kind: 'error'; message: string };

export type ToggleResult =
  | { kind: 'ok'; item: PoiRecord }
  | { kind: 'unauthorized' }
  | { kind: 'forbidden' }
  | { kind: 'not_found' }
  | { kind: 'error'; message: string };

const PIN_TYPE_LABELS: Record<PinType, string> = {
  defibrillator: 'Hjärtstartare',
  dog_waste_bin: 'Hundlatrin',
  lighting: 'Belysning',
  notice: 'Notis',
};

export const PIN_TYPES: ReadonlyArray<PinType> = [
  'defibrillator',
  'dog_waste_bin',
  'lighting',
  'notice',
];

export const PIN_KINDS: ReadonlyArray<PinKind> = ['info', 'notice'];

export function pinTypeLabel(type: PinType): string {
  return PIN_TYPE_LABELS[type];
}

export function pinKindLabel(kind: PinKind): string {
  return kind === 'info' ? 'Permanent' : 'Tidsbegränsad notis';
}

export function isActive(record: PoiRecord, now: Date = new Date()): boolean {
  if (record.deletedAt) return false;
  const from = new Date(record.activeFrom);
  if (Number.isNaN(from.getTime()) || from > now) return false;
  if (record.activeTo) {
    const to = new Date(record.activeTo);
    if (!Number.isNaN(to.getTime()) && to <= now) return false;
  }
  return true;
}

type RawPoi = {
  id: string;
  pin_type: PinType;
  kind: PinKind;
  latitude: number;
  longitude: number;
  title: string;
  body: string | null;
  active_from: string;
  active_to: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
};

function adapt(raw: RawPoi): PoiRecord {
  return {
    id: raw.id,
    pinType: raw.pin_type,
    kind: raw.kind,
    latitude: raw.latitude,
    longitude: raw.longitude,
    title: raw.title,
    body: raw.body,
    activeFrom: raw.active_from,
    activeTo: raw.active_to,
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
    deletedAt: raw.deleted_at,
  };
}

function messageForCode(code: string): string {
  switch (code) {
    case 'unauthorized':
      return 'Sessionen har gått ut. Logga in igen.';
    case 'forbidden':
      return 'Saknar behörighet att hantera POI:er.';
    case 'invalid_pin_type':
      return 'Ogiltig pin-typ.';
    case 'invalid_kind':
      return 'Ogiltig pin-variant.';
    case 'invalid_title':
      return 'Titeln måste vara mellan 1 och 120 tecken.';
    case 'body_too_long':
      return 'Beskrivningen får vara max 200 tecken.';
    case 'invalid_latitude':
      return 'Ogiltig latitud.';
    case 'invalid_longitude':
      return 'Ogiltig longitud.';
    case 'invalid_active_from':
    case 'invalid_active_to':
      return 'Ogiltig tidsstämpel.';
    case 'invalid_active_period':
      return 'Slut måste vara efter start.';
    case 'notice_requires_active_to':
      return 'Notiser måste ha ett slut-datum.';
    case 'pii_in_title_personnummer':
    case 'pii_in_body_personnummer':
      return 'Texten innehåller ett personnummer. Ta bort det.';
    case 'pii_in_title_telefon':
    case 'pii_in_body_telefon':
      return 'Texten innehåller ett telefonnummer. Ta bort det.';
    case 'pii_in_title_epost':
    case 'pii_in_body_epost':
      return 'Texten innehåller en e-postadress. Ta bort den.';
    case 'invalid_id':
      return 'Ogiltigt id.';
    case 'not_found':
      return 'Pin-en kunde inte hittas.';
    default:
      return 'Något gick fel. Försök igen.';
  }
}

function fieldForCode(code: string): string {
  if (code.startsWith('pii_in_title')) return 'title';
  if (code.startsWith('pii_in_body')) return 'body';
  if (code === 'invalid_title') return 'title';
  if (code === 'body_too_long') return 'body';
  if (code === 'invalid_latitude' || code === 'invalid_longitude') return 'location';
  if (
    code === 'invalid_active_from' ||
    code === 'invalid_active_to' ||
    code === 'invalid_active_period'
  )
    return 'period';
  if (code === 'notice_requires_active_to') return 'period';
  if (code === 'invalid_pin_type') return 'pin_type';
  if (code === 'invalid_kind') return 'kind';
  return 'payload';
}

async function readJson<T>(response: Response): Promise<T | null> {
  try {
    return (await response.json()) as T;
  } catch {
    return null;
  }
}

async function decodeError(response: Response): Promise<{ code: string; message: string }> {
  const body = await readJson<{ error?: string }>(response);
  const code = typeof body?.error === 'string' ? body.error : 'unknown';
  return { code, message: messageForCode(code) };
}

export async function listPois(): Promise<ListResult> {
  let response: Response;
  try {
    response = await fetch(API_BASE, {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
  } catch {
    return { kind: 'error', message: 'Nätverksfel — kontrollera anslutningen.' };
  }
  if (response.status === 401) return { kind: 'unauthorized' };
  if (response.status === 403) return { kind: 'forbidden' };
  if (!response.ok) return { kind: 'error', message: messageForCode('unknown') };
  const body = await readJson<{ items?: RawPoi[] }>(response);
  return { kind: 'ok', items: (body?.items ?? []).map(adapt) };
}

export async function createPoi(input: CreatePoiInput): Promise<SaveResult> {
  let response: Response;
  try {
    response = await fetch(API_BASE, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...csrfHeader(),
      },
      body: JSON.stringify({
        pin_type: input.pinType,
        kind: input.kind,
        latitude: input.latitude,
        longitude: input.longitude,
        title: input.title,
        body: input.body,
        active_from: input.activeFrom,
        active_to: input.activeTo,
      }),
    });
  } catch {
    return { kind: 'error', message: 'Nätverksfel — försök igen.' };
  }
  if (response.status === 401) return { kind: 'unauthorized' };
  if (response.status === 403) return { kind: 'forbidden' };
  if (response.status === 400) {
    const err = await decodeError(response);
    return { kind: 'invalid', field: fieldForCode(err.code), message: err.message };
  }
  if (response.status === 404) return { kind: 'not_found' };
  if (!response.ok) return { kind: 'error', message: messageForCode('unknown') };
  const raw = await readJson<RawPoi>(response);
  if (!raw) return { kind: 'error', message: messageForCode('unknown') };
  return { kind: 'ok', item: adapt(raw) };
}

export async function updatePoi(id: string, input: UpdatePoiInput): Promise<SaveResult> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE}/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...csrfHeader(),
      },
      body: JSON.stringify({
        pin_type: input.pinType,
        kind: input.kind,
        latitude: input.latitude,
        longitude: input.longitude,
        title: input.title,
        body: input.body,
        active_from: input.activeFrom,
        active_to: input.activeTo,
      }),
    });
  } catch {
    return { kind: 'error', message: 'Nätverksfel — försök igen.' };
  }
  if (response.status === 401) return { kind: 'unauthorized' };
  if (response.status === 403) return { kind: 'forbidden' };
  if (response.status === 400) {
    const err = await decodeError(response);
    return { kind: 'invalid', field: fieldForCode(err.code), message: err.message };
  }
  if (response.status === 404) return { kind: 'not_found' };
  if (!response.ok) return { kind: 'error', message: messageForCode('unknown') };
  const raw = await readJson<RawPoi>(response);
  if (!raw) return { kind: 'error', message: messageForCode('unknown') };
  return { kind: 'ok', item: adapt(raw) };
}

export async function deletePoi(id: string): Promise<ToggleResult> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE}/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: { Accept: 'application/json', ...csrfHeader() },
    });
  } catch {
    return { kind: 'error', message: 'Nätverksfel — försök igen.' };
  }
  if (response.status === 401) return { kind: 'unauthorized' };
  if (response.status === 403) return { kind: 'forbidden' };
  if (response.status === 404) return { kind: 'not_found' };
  if (!response.ok) return { kind: 'error', message: messageForCode('unknown') };
  const raw = await readJson<RawPoi>(response);
  if (!raw) return { kind: 'error', message: messageForCode('unknown') };
  return { kind: 'ok', item: adapt(raw) };
}

export async function restorePoi(id: string): Promise<ToggleResult> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE}/${encodeURIComponent(id)}/restore`, {
      method: 'POST',
      credentials: 'include',
      headers: { Accept: 'application/json', ...csrfHeader() },
    });
  } catch {
    return { kind: 'error', message: 'Nätverksfel — försök igen.' };
  }
  if (response.status === 401) return { kind: 'unauthorized' };
  if (response.status === 403) return { kind: 'forbidden' };
  if (response.status === 404) return { kind: 'not_found' };
  if (!response.ok) return { kind: 'error', message: messageForCode('unknown') };
  const raw = await readJson<RawPoi>(response);
  if (!raw) return { kind: 'error', message: messageForCode('unknown') };
  return { kind: 'ok', item: adapt(raw) };
}
